import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, Receipt, ClipboardCheck, Wrench, Wallet, Bell,
  Sparkles, ArrowRight, Check, Star, Zap, Users,
  FileText, LineChart, RefreshCw, Menu, X, Quote, PenLine, FileSpreadsheet,
  Home, BookOpen, FileSignature, Camera, KeyRound, Mail, PlayCircle,
  Instagram, ShieldCheck, MapPin, Lock,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { LogoMark } from "@/components/Logo";
import DemoAssistant from "@/components/DemoAssistant";
import { ARTICLES } from "@/data/articles";
import { BrowserMockup } from "@/components/BrowserMockup";
import { trackVisit } from "@/lib/tracking";

const NAV_LINKS = [
  { href: "#fonctionnalites", label: "Fonctionnalités" },
  { href: "#services", label: "Services" },
  { href: "#pour-qui", label: "Pour qui" },
  { href: "/tarifs", label: "Tarifs", route: true },
  { href: "/ressources", label: "Ressources", route: true },
  { href: "#temoignages", label: "Avis" },
  { href: "#faq", label: "FAQ" },
];

const FEATURES = [
  { icon: Receipt, title: "Quittances & reçus", desc: "Génération PDF en 1 clic, quittance pour paiement complet, reçu pour partiel.", flagship: true },
  { icon: FileSpreadsheet, title: "Déclaration 2044", desc: "Export préparé pour votre déclaration de revenus fonciers.", flagship: true },
  { icon: PenLine, title: "Signature électronique", desc: "Bailleur & locataire signent en un geste, PDF horodaté.", flagship: true },
  { icon: Bell, title: "Alertes intelligentes", desc: "Baux expirants, impayés, diagnostics — jamais un rappel important oublié." },
  { icon: FileText, title: "Contrats de bail", desc: "Vide, meublé, commercial. Générés en PDF avec vos infos et IBAN." },
  { icon: LineChart, title: "Suivi paiements", desc: "Statuts payé / partiel / retard. Historique par locataire." },
  { icon: ClipboardCheck, title: "États des lieux", desc: "Photos par pièce, comparaison entrée / sortie automatique." },
  { icon: Wallet, title: "Rentabilité", desc: "Rendement brut & net par bien, cashflow mensuel, export CSV." },
  { icon: Wrench, title: "Incidents & artisans", desc: "Suivi des interventions, coûts imputés au bon bien." },
];

const PREVIEWS = [
  {
    img: "/shots/dashboard.png", url: "gestimmony.fr/app", icon: LineChart,
    title: "Un tableau de bord qui va à l'essentiel",
    desc: "Loyers encaissés, taux d'occupation, impayés et alertes à traiter : votre parc en un coup d'œil, sans aucun tableur.",
    points: ["Alertes intelligentes (impayés, baux, DPE)", "Encaissements des 6 derniers mois", "Statut du parc en temps réel"],
  },
  {
    img: "/shots/tenant.png", url: "gestimmony.fr/app/locataires", icon: Users,
    title: "Vos locataires, centralisés",
    desc: "Coordonnées, profession, revenus, garant et documents : chaque fiche locataire est complète et accessible en un clic.",
    points: ["Contacts et pièces justificatives", "Invitation à l'espace locataire", "Garant et revenus suivis"],
  },
  {
    img: "/shots/receipt.png", url: "gestimmony.fr/app/quittances", icon: Receipt,
    title: "Quittances générées en un clic",
    desc: "Choisissez le bail et la période : Gestimmony produit la quittance PDF conforme, prête à déposer dans l'espace du locataire.",
    points: ["PDF conforme en 1 clic", "Quittance ou reçu partiel", "Historique complet par locataire"],
  },
];

const MODULE_GROUPS = [
  {
    label: "Gestion quotidienne",
    icon: Building2,
    items: ["Biens & lots", "Fiches locataires", "Baux vide / meublé / commercial", "Quittances & reçus", "Suivi des paiements"],
  },
  {
    label: "Documents & conformité",
    icon: FileSignature,
    items: ["États des lieux photo", "Signature électronique", "Courriers automatiques", "Révision IRL du loyer", "Suivi des assurances"],
  },
  {
    label: "Finances & fiscalité",
    icon: LineChart,
    items: ["Rentabilité brute & nette", "Cashflow mensuel", "Déclaration 2044", "Export CSV comptable", "Charges & travaux"],
  },
  {
    label: "Pilotage & services",
    icon: Sparkles,
    items: ["Tableau de bord", "Alertes intelligentes", "Assistant IA juridique", "Espace locataire dédié", "Incidents & artisans"],
  },
];

const PortalMock = () => (
  <div className="relative mx-auto max-w-sm">
    <div className="rounded-[2rem] border border-stone-200 bg-white shadow-2xl shadow-stone-900/10 p-5">
      <div className="flex items-center gap-3 border-b border-stone-100 pb-4">
        <div className="h-10 w-10 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center font-bold text-sm">MD</div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Espace locataire</div>
          <div className="text-xs text-slate-400">M. Dupont · 12 rue des Lilas</div>
        </div>
      </div>
      <div className="mt-4 space-y-2.5">
        {[
          { icon: Receipt, label: "Mes quittances", badge: "3 dispo" },
          { icon: FileText, label: "Mon bail", badge: "signé" },
          { icon: Wrench, label: "Signaler un incident" },
          { icon: Mail, label: "Mes courriers" },
        ].map((r) => (
          <div key={r.label} className="flex items-center justify-between rounded-xl border border-stone-200 px-4 py-3">
            <span className="flex items-center gap-3 text-sm font-medium text-slate-700"><r.icon className="h-4 w-4 text-[hsl(var(--primary))]" />{r.label}</span>
            {r.badge && <span className="text-[10px] font-semibold rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))] px-2 py-0.5">{r.badge}</span>}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const IrlMock = () => (
  <div className="mx-auto max-w-md rounded-2xl border border-stone-200 bg-white shadow-2xl shadow-stone-900/10 p-6">
    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><RefreshCw className="h-4 w-4 text-[hsl(var(--primary))]" /> Révision annuelle du loyer</div>
    <div className="mt-5 grid grid-cols-3 gap-3 text-center">
      <div className="rounded-xl bg-stone-50 border border-stone-200 p-3">
        <div className="text-[10px] uppercase tracking-wide text-slate-400">Ancien</div>
        <div className="mt-1 font-display text-lg font-semibold">820 €</div>
      </div>
      <div className="rounded-xl bg-[hsl(var(--accent))] border border-[hsl(var(--primary))]/20 p-3">
        <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--primary))]">IRL T2</div>
        <div className="mt-1 font-display text-lg font-semibold text-[hsl(var(--primary))]">+3,5 %</div>
      </div>
      <div className="rounded-xl bg-stone-50 border border-stone-200 p-3">
        <div className="text-[10px] uppercase tracking-wide text-slate-400">Nouveau</div>
        <div className="mt-1 font-display text-lg font-semibold text-emerald-600">848,70 €</div>
      </div>
    </div>
    <button className="mt-5 w-full rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold py-2.5 flex items-center justify-center gap-2"><Mail className="h-4 w-4" /> Générer le courrier de révision</button>
    <p className="mt-3 text-xs text-slate-400 text-center">Indice INSEE appliqué automatiquement, dans la limite légale.</p>
  </div>
);

const SignatureMock = () => (
  <div className="mx-auto max-w-md rounded-2xl border border-stone-200 bg-white shadow-2xl shadow-stone-900/10 p-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><FileSignature className="h-4 w-4 text-[hsl(var(--primary))]" /> Contrat de bail</div>
      <span className="text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5">Horodaté</span>
    </div>
    <div className="mt-5 space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
        <span className="text-sm font-medium text-slate-700">Bailleur</span>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><Check className="h-3.5 w-3.5" /> Signé</span>
      </div>
      <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
        <span className="text-sm font-medium text-slate-700">Locataire</span>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-600"><PenLine className="h-3.5 w-3.5" /> En attente</span>
      </div>
    </div>
    <div className="mt-5 border-t border-stone-100 pt-4">
      <div className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> État des lieux · photos par pièce</div>
      <div className="grid grid-cols-4 gap-2">
        {["Salon", "Cuisine", "Ch. 1", "SdB"].map((p) => (
          <div key={p} className="aspect-square rounded-lg bg-stone-100 border border-stone-200 flex items-end p-1"><span className="text-[8px] text-slate-500">{p}</span></div>
        ))}
      </div>
    </div>
  </div>
);

const SPOTLIGHTS = [
  {
    key: "portail", icon: KeyRound, badge: "Espace locataire", Mock: PortalMock,
    title: "Un portail dédié à chaque locataire",
    desc: "Vos locataires accèdent à leurs quittances, consultent leur bail, signalent un incident et reçoivent leurs courriers — sans vous appeler. Moins de sollicitations, une image plus pro.",
    points: ["Quittances disponibles automatiquement", "Déclaration d'incident en ligne", "Bail et documents consultables 24/7"],
  },
  {
    key: "irl", icon: RefreshCw, badge: "Révision IRL", Mock: IrlMock,
    title: "La révision du loyer, calculée pour vous",
    desc: "Gestimmony applique l'indice IRL de l'INSEE au bon trimestre, calcule le nouveau loyer dans la limite légale et génère le courrier de révision prêt à envoyer.",
    points: ["Indice INSEE à jour appliqué automatiquement", "Nouveau loyer plafonné selon la loi", "Courrier de révision généré en 1 clic"],
  },
  {
    key: "signature", icon: FileSignature, badge: "Signature & états des lieux", Mock: SignatureMock,
    title: "Signez vos baux et sécurisez vos états des lieux",
    desc: "Bailleur et locataire signent le bail électroniquement, avec PDF horodaté. L'état des lieux photo par pièce compare automatiquement l'entrée et la sortie — fini les litiges de dépôt.",
    points: ["Signature électronique horodatée", "États des lieux photo entrée / sortie", "Comparaison automatique pour éviter les litiges"],
  },
];

const STATS = [
  { value: "20+", label: "modules intégrés" },
  { value: "2026", label: "conforme législation" },
  { value: "0€", label: "de commissions" },
  { value: "1 clic", label: "pour une quittance" },
  { value: "100%", label: "fait en France" },
];

const AUDIENCES = [
  { icon: Users, title: "Propriétaire bailleur", desc: "Particulier avec 1 à 20 lots. Gérez sans agence, gardez la marge." },
  { icon: Building2, title: "SCI familiale", desc: "Comptes séparés, revenus fonciers prêts à déclarer via l'export 2044." },
  { icon: Home, title: "Investisseur LMNP", desc: "Location meublée : baux dédiés + suivi précis des recettes." },
  { icon: Sparkles, title: "Petite agence", desc: "Gestion déléguée pour vos clients propriétaires — un compte par propriétaire." },
];

const TESTIMONIALS = [
  { name: "Marc G.", role: "Propriétaire, 4 lots — Lyon", text: "Enfin un outil clair. Mes quittances partent toutes seules, je n'y pense plus." },
  { name: "Véronique M.", role: "SCI familiale — Bordeaux", text: "Le module Finances a remplacé mon fichier Excel. Rendement net calculé au centime près." },
  { name: "Jean-Marie D.", role: "Bailleur, 12 lots — Paris", text: "L'état des lieux avec comparaison automatique m'a évité un litige de dépôt de garantie." },
  { name: "Christelle L.", role: "Investisseuse — Nantes", text: "Interface premium et efficace. Mes locataires reçoivent leur quittance instantanément." },
  { name: "Fabrice C.", role: "Bailleur 8 lots — Rennes", text: "J'utilise le tableau de bord tous les jours. Les alertes m'ont sauvé sur 3 échéances de bail." },
  { name: "Emmanuel K.", role: "Propriétaire", text: "Simple à prendre en main, complet, français. Exactement ce que je cherchais." },
];

const FAQS = [
  { q: "À quoi sert Gestimmony ?",
    a: "Gestimmony centralise toute la gestion locative : biens, locataires, baux, quittances, états des lieux, incidents et finances. Vous gagnez du temps sur les tâches répétitives et vous ne loupez plus une échéance." },
  { q: "Est-ce vraiment adapté à un particulier ?",
    a: "Oui, l'interface a été pensée pour un bailleur qui n'est ni comptable ni juriste. En quelques minutes, vous ajoutez un bien, un locataire, un bail et vous générez la quittance du mois." },
  { q: "Est-ce que je peux personnaliser mes documents ?",
    a: "Oui : dans Paramètres vous saisissez votre raison sociale (SCI ou nom propre), votre adresse, SIRET, IBAN et logo. Toutes ces informations apparaissent dans les baux et quittances générés." },
  { q: "La signature électronique est-elle légalement valable ?",
    a: "Elle constitue une signature simple, horodatée et opposable dans le cadre de la gestion locative courante (baux d'habitation, états des lieux). Pour un usage à forte valeur juridique (commercial complexe), une signature qualifiée type Yousign/DocuSign reste recommandée." },
  { q: "Puis-je préparer ma déclaration de revenus fonciers ?",
    a: "Un export CSV prérempli reprend les lignes du formulaire 2044 (loyers encaissés, charges de copropriété, taxe foncière, travaux). Les intérêts d'emprunt et l'assurance PNO restent à ajouter manuellement selon votre situation." },
  { q: "Où sont stockées mes données ?",
    a: "En base de données sécurisée, avec isolation stricte par compte. Vous restez propriétaire de vos données à tout moment et pouvez les exporter (CSV pour les finances, PDF pour les documents)." },
  { q: "Combien ça coûte ?",
    a: "Trois formules sont disponibles (Essentiel, Patrimoine, Prestige), en mensuel sans engagement ou en annuel à −20 % avec 14 jours d'essai gratuit. En mensuel, le paiement est immédiat à la souscription ; en annuel, le premier prélèvement n'intervient qu'après les 14 jours d'essai. La TVA applicable à votre territoire est ajoutée automatiquement sur la facture." },
];

export default function LandingPage() {
  useEffect(() => { trackVisit(); }, []);
  const { user, demo } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      await demo();
      navigate("/app");
    } catch {
      setDemoLoading(false);
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const ctaPrimary = user ? { href: "/app", label: "Ouvrir mon espace" } : { href: "/register", label: "Créer mon compte" };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]" data-testid="landing-page">
      {/* NAV */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all ${
          scrolled ? "bg-white/85 backdrop-blur border-b border-stone-200" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark className="h-9" />
            <div className="font-heading font-bold text-lg tracking-tight">Gestimmony</div>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((n) =>
              n.route ? (
                <Link key={n.href} to={n.href} data-testid="nav-pricing" className="text-sm font-medium text-slate-600 hover:text-[hsl(var(--primary))] transition-colors">
                  {n.label}
                </Link>
              ) : (
                <a key={n.href} href={n.href} className="text-sm font-medium text-slate-600 hover:text-[hsl(var(--primary))] transition-colors">
                  {n.label}
                </a>
              )
            )}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/portail/login" data-testid="nav-tenant-portal" className="text-sm font-medium text-slate-600 hover:text-[hsl(var(--primary))] transition-colors">Espace locataire</Link>
            {user ? (
              <Link to="/app" data-testid="nav-open-app" className="btn-primary text-sm">Ouvrir mon espace</Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-slate-700 hover:text-[hsl(var(--primary))]">Se connecter</Link>
                <Link to="/register" data-testid="nav-register" className="btn-primary text-sm">Créer un compte</Link>
              </>
            )}
          </div>
          <button className="lg:hidden text-slate-700" onClick={() => setMobileNav(!mobileNav)} data-testid="nav-mobile-toggle">
            {mobileNav ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileNav && (
          <div className="lg:hidden bg-white border-t border-stone-200 px-6 py-4 space-y-3">
            {NAV_LINKS.map((n) =>
              n.route ? (
                <Link key={n.href} to={n.href} onClick={() => setMobileNav(false)} className="block text-sm font-medium text-slate-700">
                  {n.label}
                </Link>
              ) : (
                <a key={n.href} href={n.href} onClick={() => setMobileNav(false)} className="block text-sm font-medium text-slate-700">
                  {n.label}
                </a>
              )
            )}
            <div className="pt-3 flex gap-2">
              <Link to="/portail/login" data-testid="nav-mobile-tenant-portal" onClick={() => setMobileNav(false)} className="btn-ghost-lg text-sm flex-1 text-center">Espace locataire</Link>
              {user ? (
                <Link to="/app" className="btn-primary text-sm flex-1 text-center">Mon espace</Link>
              ) : (
                <>
                  <Link to="/login" className="btn-ghost-lg text-sm flex-1 text-center">Se connecter</Link>
                  <Link to="/register" className="btn-primary text-sm flex-1 text-center">Créer un compte</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-70" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="rise inline-flex items-center gap-2 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))] text-xs font-semibold px-3 py-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Nouveau · Version 2026
              </div>
              <h1 className="rise rise-1 mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight">
                La gestion locative,{" "}
                <span className="hero-underline">enfin claire.</span>
              </h1>
              <p className="rise rise-2 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                Biens, locataires, baux, quittances, états des lieux, rentabilité — Gestimmony rassemble
                tout ce qu'un propriétaire ou une SCI doit maîtriser, dans une interface pensée pour
                aller vite et bien.
              </p>
              <div className="rise rise-3 mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Link to={ctaPrimary.href} data-testid="hero-cta-primary" className="btn-primary inline-flex items-center gap-2 text-base">
                  {ctaPrimary.label} <ArrowRight className="h-4 w-4" />
                </Link>
                {!user && (
                  <button onClick={handleDemo} disabled={demoLoading} data-testid="hero-cta-demo" className="btn-ghost-lg inline-flex items-center gap-2 text-base disabled:opacity-60">
                    <PlayCircle className="h-4 w-4" /> {demoLoading ? "Ouverture de la démo…" : "Voir une démo"}
                  </button>
                )}
              </div>
              <div className="rise rise-4 mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
                <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Sans engagement</div>
                <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Paiement sécurisé</div>
                <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Conforme législation FR</div>
              </div>
              <div className="rise rise-4 mt-8 flex items-center gap-3" data-testid="hero-trust">
                <div className="h-9 w-9 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))] flex items-center justify-center shrink-0">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div className="text-sm">
                  <div className="text-slate-800 font-semibold">Service français, conforme à la législation</div>
                  <div className="text-xs text-slate-500">Édité par LMBS (SIREN 131 890 709) · Données hébergées en France</div>
                </div>
              </div>
            </div>

            {/* Device mockup */}
            <div className="lg:col-span-6 relative rise rise-2">
              <BrowserMockup src="/shots/dashboard.png" alt="Tableau de bord Gestimmony — vue d'ensemble du parc locatif" url="gestimmony.fr/app" priority />
              {/* Floating chip */}
              <div className="absolute -bottom-5 -left-5 bg-white border border-stone-200 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 hidden sm:flex">
                <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <Check className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-900">Quittance envoyée</div>
                  <div className="text-[10px] text-slate-500">M. Dupont · +820 € · aujourd'hui</div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white border border-stone-200 rounded-xl shadow-lg px-3 py-2 hidden sm:flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                <div className="text-xs font-medium text-slate-700">Bail expire dans 45 j</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="fonctionnalites" className="py-20 sm:py-28 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge variant="outline" className="rounded-full border-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] bg-[hsl(var(--accent))]">
              Fonctionnalités
            </Badge>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
              Pilotez vos locations{" "}
              <span className="text-[hsl(var(--primary))]">en toute simplicité.</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Une plateforme complète, du premier locataire à la déclaration fiscale.
              Zéro ligne d'Excel, zéro tableur à mettre à jour.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-${i}`}
                className={`card-hover group relative rounded-2xl ${
                  f.flagship
                    ? "border-2 border-[hsl(var(--primary))]/30 bg-[hsl(var(--accent))]/40 ring-1 ring-[hsl(var(--primary))]/10 shadow-sm p-6"
                    : "bg-white border border-stone-200 p-5"
                }`}
              >
                {f.flagship && (
                  <span
                    data-testid={`feature-badge-${i}`}
                    className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))] text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5"
                  >
                    <Star className="h-3 w-3" /> Populaire
                  </span>
                )}
                <div className={`rounded-xl flex items-center justify-center transition-colors ${
                  f.flagship
                    ? "h-12 w-12 bg-[hsl(var(--primary))] text-white"
                    : "h-10 w-10 bg-[hsl(var(--accent))] text-[hsl(var(--primary))] group-hover:bg-[hsl(var(--primary))] group-hover:text-white"
                }`}>
                  <f.icon className={f.flagship ? "h-6 w-6" : "h-5 w-5"} />
                </div>
                <div className={`font-heading font-semibold text-slate-900 ${f.flagship ? "mt-5 text-lg" : "mt-4"}`}>{f.title}</div>
                <div className={`mt-1 text-slate-500 leading-relaxed ${f.flagship ? "text-sm" : "text-xs"}`}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VUE D'ENSEMBLE — TOUS LES MODULES */}
      <section id="tout" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge variant="outline" className="rounded-full border-amber-500/30 text-amber-700 bg-amber-50">
              Vue d'ensemble
            </Badge>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
              Tout ce que fait Gestimmony,{" "}
              <span className="text-[hsl(var(--primary))]">au même endroit.</span>
            </h2>
            <p className="mt-4 text-slate-600">
              De la fiche locataire à la déclaration fiscale, plus de 20 modules couvrent
              l'intégralité de votre gestion locative. Rien ne vous échappe, rien ne traîne dans un tableur.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {MODULE_GROUPS.map((g, gi) => (
              <div key={g.label} data-testid={`module-group-${gi}`} className="card-hover rounded-2xl border border-stone-200 bg-white p-6">
                <div className="h-11 w-11 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center">
                  <g.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-heading font-semibold text-slate-900">{g.label}</div>
                <ul className="mt-4 space-y-2.5">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESSOURCES */}
      <section id="ressources" className="py-20 sm:py-28 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div className="max-w-2xl">
              <Badge variant="outline" className="rounded-full border-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] bg-[hsl(var(--accent))]">
                <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Ressources & guides
              </Badge>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                Des guides pour{" "}
                <span className="text-[hsl(var(--primary))]">décider sans vous tromper.</span>
              </h2>
              <p className="mt-4 text-slate-600">
                Révision IRL, déclaration 2044, fiscalité du meublé : nos articles répondent
                aux vraies questions des propriétaires bailleurs.
              </p>
            </div>
            <Link to="/ressources" data-testid="landing-resources-all" className="btn-primary shrink-0 inline-flex items-center gap-2 text-sm">
              Voir toutes les ressources <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ARTICLES.slice(0, 3).map((a) => (
              <Link
                key={a.slug}
                to={`/ressources/${a.slug}`}
                data-testid={`landing-article-${a.slug}`}
                className="group flex flex-col rounded-2xl border border-stone-200 bg-white overflow-hidden card-hover"
              >
                <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                  <img src={a.cover} alt={a.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <div className="text-xs font-medium text-teal-700 mb-2">{a.category}</div>
                  <h3 className="font-heading font-semibold text-slate-900 text-lg leading-snug group-hover:text-[hsl(var(--primary))] transition-colors">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))]">
                    Lire l'article <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* APERÇU PRODUIT */}
      <section id="apercu" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge variant="outline" className="rounded-full border-amber-500/30 text-amber-700 bg-amber-50">
              Aperçu du produit
            </Badge>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
              Voyez Gestimmony{" "}
              <span className="text-[hsl(var(--primary))]">en action.</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Des écrans réels de l'application, pas des maquettes. Clair, rapide, pensé pour les bailleurs.
            </p>
          </div>

          <div className="mt-16 space-y-20 lg:space-y-28">
            {PREVIEWS.map((pv, i) => (
              <div key={pv.title} className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <BrowserMockup src={pv.img} alt={pv.title} url={pv.url} />
                </div>
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="h-11 w-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))] flex items-center justify-center">
                    <pv.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl sm:text-3xl font-semibold tracking-tight">{pv.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{pv.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {pv.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3 text-sm text-slate-700">
                        <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))] flex items-center justify-center">
                          <Check className="h-3 w-3" />
                        </span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES VEDETTES */}
      <section id="services" className="py-20 sm:py-28 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge variant="outline" className="rounded-full border-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] bg-[hsl(var(--accent))]">
              Services qui font la différence
            </Badge>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
              Bien plus qu'un{" "}
              <span className="text-[hsl(var(--primary))]">tableur amélioré.</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Des services que la plupart des propriétaires gèrent encore à la main —
              et que Gestimmony automatise de bout en bout.
            </p>
          </div>

          <div className="mt-16 space-y-20 lg:space-y-28">
            {SPOTLIGHTS.map((s, i) => (
              <div key={s.key} data-testid={`spotlight-${s.key}`} className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <s.Mock />
                </div>
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))] text-xs font-semibold px-3 py-1.5">
                    <s.icon className="h-3.5 w-3.5" /> {s.badge}
                  </div>
                  <h3 className="mt-5 font-display text-2xl sm:text-3xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{s.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {s.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3 text-sm text-slate-700">
                        <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))] flex items-center justify-center">
                          <Check className="h-3 w-3" />
                        </span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 bg-[hsl(var(--primary))] text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
            {STATS.map((s, i) => (
              <div key={i} className="text-center sm:text-left">
                <div className="font-display text-3xl sm:text-4xl font-semibold">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-white/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section id="pour-qui" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <Badge variant="outline" className="rounded-full border-amber-500/30 text-amber-700 bg-amber-50">
                Pour qui
              </Badge>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                Des solutions{" "}
                <span className="text-[hsl(var(--primary))]">pour tous les bailleurs.</span>
              </h2>
              <p className="mt-4 text-slate-600">
                Que vous ayez un studio ou vingt appartements, en nom propre ou en SCI,
                Gestimmony s'adapte à votre organisation.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {AUDIENCES.map((a) => (
                <div key={a.title} className="card-hover bg-white border border-stone-200 rounded-2xl p-6">
                  <div className="h-11 w-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))] flex items-center justify-center">
                    <a.icon className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-heading font-semibold text-lg">{a.title}</div>
                  <div className="mt-1 text-sm text-slate-600">{a.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="temoignages" className="py-20 sm:py-28 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <Badge variant="outline" className="rounded-full border-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] bg-[hsl(var(--accent))]">
              Programme bêta
            </Badge>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
              Les premiers retours de{" "}
              <span className="text-[hsl(var(--primary))]">nos bêta-testeurs.</span>
            </h2>
            <p className="mt-4 text-sm text-slate-500">
              Gestimmony est un service récent : voici des retours représentatifs recueillis auprès des
              bailleurs qui testent l'outil. Les prénoms ont été modifiés pour préserver leur anonymat.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className="bg-[hsl(var(--background))] border border-stone-200 rounded-2xl p-6">
                <Quote className="h-6 w-6 text-[hsl(var(--primary))]/30" />
                <blockquote className="mt-3 text-sm text-slate-700 leading-relaxed">"{t.text}"</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center text-xs font-bold">
                    {t.name.split(" ").map(p=>p[0]).join("")}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge variant="outline" className="rounded-full border-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] bg-[hsl(var(--accent))]">
              FAQ
            </Badge>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight">Questions fréquentes.</h2>
          </div>
          <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem value={`item-${i}`} key={i} className="border-b border-stone-200">
                <AccordionTrigger className="font-heading text-left font-semibold hover:no-underline hover:text-[hsl(var(--primary))]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-[hsl(var(--primary))] text-white p-10 sm:p-16">
            <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                  Prêt·e à reprendre le contrôle de votre parc ?
                </h3>
                <p className="mt-4 text-white/80 text-base">
                  Démarrez en 2 minutes, ajoutez votre premier bien, générez votre première quittance.
                  Sans engagement en mensuel, résiliable à tout moment.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
                <Link to={ctaPrimary.href} data-testid="footer-cta-primary" className="inline-flex items-center justify-center gap-2 bg-white text-[hsl(var(--primary))] font-semibold rounded-full px-6 py-3 hover:bg-stone-100 transition-colors">
                  {ctaPrimary.label} <ArrowRight className="h-4 w-4" />
                </Link>
                {!user && (
                  <button onClick={handleDemo} disabled={demoLoading} data-testid="footer-cta-demo" className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-semibold rounded-full px-6 py-3 hover:bg-white/10 transition-colors disabled:opacity-60">
                    <PlayCircle className="h-4 w-4" /> {demoLoading ? "Ouverture…" : "Voir une démo"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-stone-200 pt-10 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <LogoMark className="h-8" />
              <div className="font-heading font-bold">Gestimmony</div>
              <span className="text-xs text-slate-400 ml-2">© 2026 — Fait en France</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-slate-500">
              <a href="#fonctionnalites" className="hover:text-[hsl(var(--primary))]">Fonctionnalités</a>
              <Link to="/tarifs" className="hover:text-[hsl(var(--primary))]">Tarifs</Link>
              <Link to="/ressources" className="hover:text-[hsl(var(--primary))]">Ressources</Link>
              <a href="#faq" className="hover:text-[hsl(var(--primary))]">FAQ</a>
              <Link to="/cgu" className="hover:text-[hsl(var(--primary))]">CGU</Link>
              <Link to="/confidentialite" className="hover:text-[hsl(var(--primary))]">Confidentialité</Link>
              <Link to="/mentions-legales" className="hover:text-[hsl(var(--primary))]">Mentions légales</Link>
              <Link to="/contact" className="hover:text-[hsl(var(--primary))]">Contact</Link>
              <a href="https://www.instagram.com/gestimmony" target="_blank" rel="noopener noreferrer" aria-label="Instagram Gestimmony" data-testid="footer-instagram" className="inline-flex items-center gap-1.5 hover:text-[hsl(var(--primary))]">
                <Instagram className="h-4 w-4" /> Instagram
              </a>
              {user ? (
                <Link to="/app" className="hover:text-[hsl(var(--primary))]">Mon espace</Link>
              ) : (
                <Link to="/login" className="hover:text-[hsl(var(--primary))]">Se connecter</Link>
              )}
            </div>
          </div>

          {/* Bande de réassurance / identité légale (crédibilité + référencement) */}
          <div className="mt-8 pt-6 border-t border-stone-200 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Conforme RGPD</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Paiement sécurisé Stripe</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Données hébergées en France (OVH)</span>
            <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Sans engagement en mensuel</span>
          </div>
          <p className="mt-4 text-[11px] leading-relaxed text-slate-400 max-w-3xl" data-testid="footer-legal-identity">
            Gestimmony est édité par la société <span className="font-medium text-slate-500">LMBS</span>, SAS au capital de 250 €,
            immatriculée au RCS de Fort-de-France sous le SIREN <span className="font-medium text-slate-500">131 890 709</span>.
            Contact : <a href="mailto:contact@gestimmony.fr" className="hover:text-[hsl(var(--primary))]">contact@gestimmony.fr</a>.
          </p>
        </div>
      </footer>

      <DemoAssistant />
    </div>
  );
}
